// redux 3.0 declarations don't support typed actions
// 4.0 does, but it also has breaking changes and update might be problematic
// so let's copy 4.0 declarations and change them a bit to be compatible with 3.0 implementation
// also some stricter types...

export * from 'redux';

import { DeepPartial } from './utils';

export interface Action<TType extends string = string> { type: TType; }

export interface AnyAction extends Action { [extraProps: string]: any; }

export interface Reducer<S = any, A extends Action = AnyAction> {
    (state: S | undefined, action: A): S;
}

export interface Dispatch<A extends Action = AnyAction> { <T extends A>(action: T): T; }

export interface Unsubscribe { (): void; }

export type Observable<T> = {
    subscribe: (observer: Observer<T>) => { unsubscribe: Unsubscribe };
    [Symbol.observable](): Observable<T>;
}


export type Observer<T> = { next?(value: T): void; }

export interface Store<S = any, A extends Action = AnyAction> {
    dispatch: Dispatch<A>;
    getState(): S;
    subscribe(listener: () => void): Unsubscribe;
    replaceReducer(nextReducer: Reducer<S, A>): void;
    [Symbol.observable](): Observable<S>;
}


export type StoreEnhancer<Ext = {}, StateExt = {}> = (
    next: StoreEnhancerStoreCreator
) => StoreEnhancerStoreCreator<Ext, StateExt>
export type StoreEnhancerStoreCreator<Ext = {}, StateExt = {}> = <
    S = any,
    A extends Action = AnyAction
    >(
    reducer: Reducer<S, A>,
    preloadedState?: DeepPartial<S>
) => Store<S & StateExt, A> & Ext

export interface StoreCreator {
    <S, A extends Action, Ext, StateExt>(
        reducer: Reducer<S, A>,
        enhancer?: StoreEnhancer<Ext, StateExt>
    ): Store<S & StateExt, A> & Ext
}

import { createStore as originalCreateStore } from 'redux';
export const createStore: StoreCreator = originalCreateStore;

export interface MiddlewareAPI<D extends Dispatch = Dispatch, S = any> {
    dispatch: D
    getState(): S
}

export interface Middleware<
    _DispatchExt = {},
    S = any,
    D extends Dispatch = Dispatch
    > {
    (api: MiddlewareAPI<D, S>): (
        next: Dispatch<AnyAction>
    ) => (action: any) => any
}

import { applyMiddleware as originalApplyMiddleWare } from 'redux';

export function applyMiddleware(): StoreEnhancer
export function applyMiddleware<Ext1, S>(
    middleware1: Middleware<Ext1, S, any>
): StoreEnhancer<{ dispatch: Ext1 }>
export function applyMiddleware<Ext1, Ext2, S>(
    middleware1: Middleware<Ext1, S, any>,
    middleware2: Middleware<Ext2, S, any>
): StoreEnhancer<{ dispatch: Ext1 & Ext2 }>
export function applyMiddleware<Ext1, Ext2, Ext3, S>(
    middleware1: Middleware<Ext1, S, any>,
    middleware2: Middleware<Ext2, S, any>,
    middleware3: Middleware<Ext3, S, any>
): StoreEnhancer<{ dispatch: Ext1 & Ext2 & Ext3 }>
export function applyMiddleware<Ext1, Ext2, Ext3, Ext4, S>(
    middleware1: Middleware<Ext1, S, any>,
    middleware2: Middleware<Ext2, S, any>,
    middleware3: Middleware<Ext3, S, any>,
    middleware4: Middleware<Ext4, S, any>
): StoreEnhancer<{ dispatch: Ext1 & Ext2 & Ext3 & Ext4 }>
export function applyMiddleware<Ext1, Ext2, Ext3, Ext4, Ext5, S>(
    middleware1: Middleware<Ext1, S, any>,
    middleware2: Middleware<Ext2, S, any>,
    middleware3: Middleware<Ext3, S, any>,
    middleware4: Middleware<Ext4, S, any>,
    middleware5: Middleware<Ext5, S, any>
): StoreEnhancer<{ dispatch: Ext1 & Ext2 & Ext3 & Ext4 & Ext5 }>
export function applyMiddleware<Ext, S = any>(
    ...middlewares: Middleware<any, S, any>[]
): StoreEnhancer<{ dispatch: Ext }>
export function applyMiddleware(...args: any[]): any { return !args.length ? originalApplyMiddleWare() : originalApplyMiddleWare(...args); }


// and redux-thunk

export * from 'redux-thunk';

import { default as originalThunk } from 'redux-thunk';

export interface ThunkDispatch<S, E, A extends Action> {
    <T extends A>(action: T): T;
    <R>(asyncAction: ThunkAction<R, S, E, A>): R;
}

export type ThunkAction<R, S, E, A extends Action> = (
    dispatch: ThunkDispatch<S, E, A>,
    getState: () => S,
    extraArgument: E
) => R;

export type ThunkMiddleware<S = {}, A extends Action = AnyAction, E = undefined> = Middleware<ThunkDispatch<S, E, A>, S, ThunkDispatch<S, E, A>>;

export const thunk = originalThunk as ThunkMiddleware & { withExtraArgument<E>(extraArgument: E): ThunkMiddleware<{}, AnyAction, E> }

// react-redux should also be here, but there's too much export and too many differences...
// some hacks though:

export interface DispatchProps {
    dispatch: ThunkDispatch<any, void, AnyAction>;
}

import { connect as originalConnect, MapStateToPropsParam, Component, ComponentClass } from 'react-redux';

export interface ComponentDecorator<TMergedProps, TOwnProps> {
    <C extends Component<TOwnProps & TMergedProps>>(component: C): ComponentClass<TOwnProps> & {
        [S in keyof C]: C[S];
    };
}

export const connect = originalConnect as <TStateProps, _no_dispatch, TOwnProps>(
    mapStateToProps: MapStateToPropsParam<TStateProps, TOwnProps>,
    mapDispatchToProps: null | undefined,
    mergeProps: null | undefined,
    options: {}
) => ComponentDecorator<DispatchProps & TStateProps, TOwnProps>;

// and react-router-redux

export * from 'react-router-redux';

import {
    LOCATION_CHANGE as ORIGINAL_LOCATION_CHANGE, LocationActionPayload,
    push as originalPush, replace as originalReplace, go as originalGo, goForward as originalGoForward, goBack as originalGoBack,
    routerReducer as originalRouterReducer, syncHistoryWithStore as originalSyncHistoryWithStore, routerMiddleware as originalRouterMiddleware,
    RouterState, SyncHistoryWithStoreOptions, HistoryUnsubscribe
} from 'react-router-redux';
import { History, LocationDescriptor } from "history";

export const LOCATION_CHANGE: 'LOCATION_CHANGE' = ORIGINAL_LOCATION_CHANGE as any;

export interface RouterAction extends Action<typeof LOCATION_CHANGE> {
    payload: LocationActionPayload;
}

export type LocationAction = (nextLocation: LocationDescriptor) => RouterAction;
export type GoAction = (n: number) => RouterAction;
export type NavigateAction = () => RouterAction;

export const push = originalPush as LocationAction;
export const replace = originalReplace as LocationAction;
export const go = originalGo as GoAction;
export const goForward = originalGoForward as NavigateAction;
export const goBack = originalGoBack as NavigateAction;

export const routerReducer = originalRouterReducer as (state?: RouterState, action?: RouterAction) => RouterState;
export const syncHistoryWithStore = originalSyncHistoryWithStore as <S, A extends AnyAction>(history: History, store: Store<S, A>, options?: SyncHistoryWithStoreOptions) => History & HistoryUnsubscribe;
export const routerMiddleware = originalRouterMiddleware as (history: History) => Middleware;
